// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import { Description, LgSpacing, XsSpacing } from '@alto/design-system';
import React, { useEffect } from 'react';
import { turnOffSplitPayment, updateOrder } from '~shared/actions/checkout';
import { type PaymentMethodType } from '~shared/constants';
import { useCartUsers } from '~shared/features/cart/hooks/useCartUsers';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { getCheckoutPaymentAmountErrors, getCheckoutPaymentMethodErrors } from '~shared/features/checkout/helpers';
import { getShowingValidationErrors } from '~shared/features/checkout/selectors/getCheckout';
import { getOrder } from '~shared/features/checkout/selectors/getOrder';
import { findMatchingPaymentMethod } from '~shared/features/payment-methods/helpers';
import { hasProgramDispensingFee } from '~shared/features/pricing/orderPricingSplitPayment';
import { getIsSplitPaymentEnabled } from '~shared/features/ui/selectors/getCart';
import { useOrderPricing } from '~shared/queries';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type ShipmentPaymentMethod } from '~shared/types';
import { CheckoutSplitPaymentSelector } from './CheckoutSplitPaymentSelector';
import { PaymentMethodRow } from './PaymentMethodRow';

type Props = {
  readonly handleNativePay?: (paymentMethodType: PaymentMethodType) => void;
  readonly shipmentPaymentMethods?: ShipmentPaymentMethod[];
  readonly hasCopay: boolean;
  readonly disabled?: boolean;
};

export const CheckoutPaymentSelector = ({ handleNativePay, hasCopay, shipmentPaymentMethods, disabled }: Props) => {
  const dispatch = useDispatchShared();
  const { isPaymentMethodRequired, orderPricing } = useOrderPricing();
  const showingValidationErrors = useSelectorShared(getShowingValidationErrors);
  const isSplitPaymentEnabled = useSelectorShared(getIsSplitPaymentEnabled);
  // the total cost used as the anchor point for split payments.
  // does not include tips and other fees (i.e delivery)
  const splitPaymentTotalCost = orderPricing?.subtotal || null;
  const orderProgramDispensingFeeApplies = hasProgramDispensingFee(orderPricing?.line_items);
  const order = useSelectorShared(getOrder);
  const { payment_method_id, second_payment_method_id } = order;
  const { paymentMethods } = useGetPaymentMethods();
  const paymentMethod = findMatchingPaymentMethod(paymentMethods, payment_method_id);
  const secondPaymentMethod = findMatchingPaymentMethod(paymentMethods, second_payment_method_id);
  const { paymentMethodValidationErrors } = getCheckoutPaymentMethodErrors({
    order,
    paymentMethods,
    isPaymentMethodRequired,
    isSplitPaymentEnabled,
  });
  const { paymentAmountValidationErrors } = getCheckoutPaymentAmountErrors({
    order,
    isSplitPaymentEnabled,
    splitPaymentsTotal: splitPaymentTotalCost,
    isPaymentMethodRequired,
  });

  const { users } = useCartUsers();
  const { deliveryMethods } = useCartDeliveryMethods();
  const hasSplitContexts = deliveryMethods.length > 1 || users.length > 1;
  // isSplitPaymentEnabled = user turned on split pay (Redux). shouldDisplaySplitPayment = order allows split UI:
  // subtotal, copay, single shipment/recipient, and no program dispensing fee (see useOrderPricing line_items).
  const shouldDisplaySplitPayment =
    !!splitPaymentTotalCost && !hasSplitContexts && hasCopay && !orderProgramDispensingFeeApplies;

  // Split pay is not allowed when a program dispensing fee applies. If the user had split enabled earlier,
  // turn it off and clear second-method and amount fields on the order so we do not submit stale split state.
  useEffect(() => {
    if (!orderProgramDispensingFeeApplies || !isSplitPaymentEnabled) {
      return;
    }
    dispatch(turnOffSplitPayment());
    dispatch(
      updateOrder({
        second_payment_method_id: undefined,
        second_payment_method_amount: undefined,
        payment_method_amount: undefined,
      }),
    );
  }, [dispatch, isSplitPaymentEnabled, orderProgramDispensingFeeApplies]);

  const errors = showingValidationErrors ? [...paymentAmountValidationErrors, ...paymentMethodValidationErrors] : [];

  return (
    <>
      <PaymentMethodRow
        disabled={disabled}
        handleNativePay={handleNativePay}
        paymentMethod={paymentMethod}
        isSecondaryPaymentMethod={false}
        splitPaymentAmountsEligible={shouldDisplaySplitPayment}
        splitPaymentTotalCost={splitPaymentTotalCost}
        hasCopay={hasCopay}
        hasSplitContexts={hasSplitContexts}
        shipmentPaymentMethods={shipmentPaymentMethods}
        isSplitPaymentEnabled={isSplitPaymentEnabled}
      />
      {shouldDisplaySplitPayment && isSplitPaymentEnabled ? (
        <>
          <XsSpacing />
          <PaymentMethodRow
            isSecondaryPaymentMethod
            handleNativePay={handleNativePay}
            paymentMethod={secondPaymentMethod}
            splitPaymentAmountsEligible={shouldDisplaySplitPayment}
            splitPaymentTotalCost={splitPaymentTotalCost}
            hasCopay={hasCopay}
            hasSplitContexts={hasSplitContexts}
            shipmentPaymentMethods={shipmentPaymentMethods}
            isSplitPaymentEnabled={isSplitPaymentEnabled}
          />
        </>
      ) : null}
      {errors?.length ? (
        <>
          <XsSpacing />
          <Description color={COLORS.TEXT_COLORS.DANGER}>{errors.map((e) => e.message).join(' ')}</Description>
        </>
      ) : null}
      {shouldDisplaySplitPayment ? (
        <CheckoutSplitPaymentSelector
          isSplitPaymentEnabled={isSplitPaymentEnabled}
          paymentMethod={paymentMethod}
        />
      ) : (
        <LgSpacing />
      )}
    </>
  );
};
